import { alpha, Components, Theme } from '@mui/material'

import { themeShadows } from './themeColors'

interface ComponentsProps extends Components<Omit<Theme, 'components'>> {
  MuiExpansionPanel: any
}

/**
 * Estilos para componentes MUI
 * @author isi-template
 */
export const components: ComponentsProps = {
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 400,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const color = ownerState.color

        if (typeof color !== 'string' || color === 'default') return {}

        const paletteKey = color as keyof typeof theme.palette
        const paletteColor = theme.palette[paletteKey] as any

        if (paletteColor && paletteColor.softBgColor) {
          if (ownerState.variant === 'filled') {
            return {
              backgroundColor: paletteColor.strongBgColor,
              color: paletteColor.strongTextColor,
              border: `1px solid ${paletteColor.borderMedium}`,
              fontWeight: 400,
            }
          }

          if (ownerState.variant === 'outlined') {
            return {
              backgroundColor: 'transparent',
              color: paletteColor.softTextColor,
              border: `1px solid ${paletteColor.borderStrong}`,
              fontWeight: 400,
              '&:hover': {
                backgroundColor: paletteColor.hoverBgColor,
              },
            }
          }
        }
        return {}
      },
    },
  },

  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      '*': { boxSizing: 'border-box' },
      html: {
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased',
        height: '100%',
        width: '100%',
      },
      body: {
        height: '100%',

        // Envolvemos los estilos del scroll en una media query de interacción
        '@media (hover: hover) and (pointer: fine)': {
          scrollbarColor: theme.palette.mode === 'dark' ? '#6b6b6b #2b2b2b' : '#b0b0b0 #f1f1f1',
          scrollbarWidth: 'thin',

          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? '#6b6b6b' : '#b0b0b0',
            borderRadius: '4px',
            border: `2px solid ${theme.palette.mode === 'dark' ? '#1e1e1e' : '#f1f1f1'}`,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#909090' : '#8b8b8b',
            },
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f1f1f1',
          },
        },
      },
      a: { textDecoration: 'none', color: 'inherit' },
      '#root': { height: '100%' },
      '#nprogress .bar': { zIndex: '2000 !important' },
    }),
  },

  MuiFab: {
    styleOverrides: {
      root: { boxShadow: themeShadows[12] },
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        '&:before': { display: 'none' },
      },
    },
  },

  MuiExpansionPanel: {
    styleOverrides: {
      root: {
        '&:before': { display: 'none' },
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: { fontSize: '0.6875rem' }, // ✅ se mantiene, tooltips necesitan ser más pequeños. Equivale a ~11px pero relativo
    },
  },

  MuiTextField: {
    defaultProps: {
      size: 'small',
    },
  },

  MuiAutocomplete: {
    defaultProps: {
      size: 'small',
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-disabled': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',

          '& .MuiOutlinedInput-notchedOutline': {
            borderStyle: 'dashed',
            borderColor: theme.palette.text.disabled,
            borderWidth: '1px',
          },

          '& .MuiInputBase-input': {
            opacity: 1,
            color: theme.palette.text.secondary,
            WebkitTextFillColor: theme.palette.text.secondary,
            fontWeight: 500,
          },
        },

        '&.Mui-readOnly, & .MuiInputBase-input[readonly]': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.04)
              : alpha(theme.palette.primary.main, 0.02),
          cursor: 'default',

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },

          '& .MuiInputBase-input': {
            cursor: 'default',
            fontWeight: 400,
          },
        },
      }),
    },
  },

  MuiSelect: {
    defaultProps: {
      size: 'small',
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        '& .MuiSvgIcon-root': { fontSize: 24 }, // ✅ se mantiene, es tamaño de ícono SVG
      },
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: {
        '& .MuiSvgIcon-root': { fontSize: 24 }, // ✅ se mantiene, es tamaño de ícono SVG
      },
    },
  },

  // /** Uso para cambio de fuente a 14 px */
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        // Tamaño base por defecto (Medium): 14px
        '& .MuiFormControlLabel-label': {
          fontSize: '0.875rem',
        },

        // Ajuste automático cuando el Checkbox/Radio es Small (12px)
        '&:has(.MuiCheckbox-sizeSmall), &:has(.MuiRadio-sizeSmall)': {
          '& .MuiFormControlLabel-label': {
            fontSize: '0.75rem',
          },
        },

        // Ajuste automático cuando el Checkbox/Radio es Large (16px)
        '&:has(.MuiCheckbox-sizeLarge), &:has(.MuiRadio-sizeLarge)': {
          '& .MuiFormControlLabel-label': {
            fontSize: '1rem',
            fontWeight: 500,
          },
        },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '6px 16px', // ajusta según tu diseño
      },
      head: {
        fontWeight: 600,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: '10px', // Un borde un poco más redondo para verse moderno
        alignItems: 'center',
      },
      // Sobreescribimos la variante 'standard' (la que se usa por defecto)
      standard: ({ theme, ownerState }) => {
        const severity = ownerState.severity || 'info'
        const colorDef = (theme.palette as any)[severity]

        // Si por alguna razón el color no pasó por nuestro motor, no lo rompemos
        if (!colorDef || !colorDef.softBgColor) return {}

        return {
          backgroundColor: colorDef.softBgColor,
          color: colorDef.softTextColor,
          border: `1px solid ${colorDef.borderColor}`,
          // Tintamos el ícono con el color puro
          '& .MuiAlert-icon': {
            color: colorDef.main,
          },
        }
      },
    },
  },
}
